import { useDispatch } from 'react-redux';

import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import {
  SelectElement,
  SelectElementProps,
} from '@opendoor/cinderblocks/growth/novo/inputs/SelectInput';

import { trackAnswerChange } from 'src/components/fields/trackAnswerChange';
import { getErrorMessage } from 'src/components/flowPage/twoColumn/ErrorMessages';
import { DropdownNumberRangeQuestion, FlowQuestion } from 'src/lib/flow/FlowQuestion';
import { checkValidation } from 'src/lib/flow/validation';
import { changeAnswers } from 'src/store/sellerInput';

type NumberRangeSelectInputFieldProps = {
  question: DropdownNumberRangeQuestion;
  value?: { max_price?: number; min_price?: number };
  showValidation?: boolean;
};

const NumberRangeSelectInputField: React.FC<NumberRangeSelectInputFieldProps> = ({
  question,
  showValidation,
  value,
}) => {
  const dispatch = useDispatch();
  const { key, maxValue, minValue, increments } = question;

  const max = maxValue ? Number.parseInt(`${maxValue}`, 10) : 10000000;
  const min = minValue ? Number.parseInt(`${minValue}`, 10) : 0;
  const inc = increments ? increments : 50000;

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  const options = Array(Math.floor((max - min) / inc))
    .fill(0)
    .map((_value, idx) => {
      return {
        label: formatter.format(idx * inc),
        value: `${idx * inc}`,
      };
    });

  let hasError = false;
  let errorText: string | undefined | null;
  const result = checkValidation(question as FlowQuestion, value);

  if (showValidation || (!result.isValid && result.type !== 'required')) {
    hasError = !result.isValid;
    errorText = getErrorMessage(result);
  }

  const onChange = (valueKey: string) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    const answer = { ...value, ...{ [valueKey]: event.target.value } };
    dispatch(changeAnswers([{ key: question.key, answer }]));
    trackAnswerChange(question.key, answer);
  };

  return (
    <>
      <Field<SelectElementProps>
        InputComponent={SelectElement}
        errorText={errorText}
        input={{
          error: hasError,
          name: `${key}-minimum`,
          id: `${key}-minimum`,
          placeholder: 'Any',
          onChange: onChange('min_price'),
          defaultValue: value?.min_price,
          options,
        }}
        //isSearchable
        label="Minimum Amount"
      />
      <Field<SelectElementProps>
        InputComponent={SelectElement}
        input={{
          error: hasError,
          name: `${key}-maximum`,
          id: `${key}-maximum`,
          placeholder: 'Any',
          onChange: onChange('max_price'),
          defaultValue: value?.max_price,
          options,
        }}
        //isSearchable
        label="Maximum Amount"
      />
    </>
  );
};

export default NumberRangeSelectInputField;
